import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { healthCheck, listDocuments, getSCGRules } from '../services/api';
import { getQueryAnalytics, getCostAnalytics } from '../services/analyticsApi';
import {
  DocumentTextIcon,
  CogIcon,
  ScissorsIcon,
  BeakerIcon,
  EyeSlashIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  ShieldCheckIcon,
  ServerIcon,
  CpuChipIcon,
  CircleStackIcon
} from '@heroicons/react/24/outline';
import ClassificationBadge from '../components/ClassificationBadge';

// Animated count-up hook
function useCountUp(target, duration = 1000) {
  const [value, setValue] = useState(0);
  const prevTarget = useRef(0);

  useEffect(() => {
    if (target === prevTarget.current) return;
    const startValue = prevTarget.current;
    prevTarget.current = target;
    const diff = target - startValue;
    if (diff === 0) return;

    const startTime = performance.now();
    let rafId;
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const fraction = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - fraction, 3);
      setValue(Math.round(startValue + diff * eased));
      if (fraction < 1) {
        rafId = requestAnimationFrame(animate);
      }
    };
    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration]);

  return value;
}

export default function Dashboard() {
  const { data: healthData } = useQuery({
    queryKey: ['health'],
    queryFn: healthCheck,
    refetchInterval: 30000,
    retry: 2,
    enabled: true,
  });

  const { data: documentsData } = useQuery({
    queryKey: ['documents-summary'],
    queryFn: () => listDocuments(10),
    retry: 2,
    enabled: true,
  });

  const { data: scgData } = useQuery({
    queryKey: ['scg-summary'],
    queryFn: getSCGRules,
    retry: 2,
    enabled: true,
  });

  const { data: queryAnalytics } = useQuery({
    queryKey: ['dashboard-query-analytics'],
    queryFn: () => getQueryAnalytics('all'),
    refetchInterval: 30000,
    retry: 2,
  });

  const { data: costAnalytics } = useQuery({
    queryKey: ['dashboard-cost-analytics'],
    queryFn: () => getCostAnalytics('all'),
    refetchInterval: 30000,
    retry: 2,
  });

  const quickActions = [
    {
      name: 'Classify',
      description: 'Analyze document security classification',
      href: '/classify',
      icon: DocumentTextIcon,
      accentColor: 'var(--spotlight-gold)'
    },
    {
      name: 'Tearline',
      description: 'Sanitize for lower classifications',
      href: '/tearline',
      icon: ScissorsIcon,
      accentColor: 'var(--class-unclassified-text)'
    },
    {
      name: 'FOIA',
      description: 'Process FOIA redactions',
      href: '/foia',
      icon: EyeSlashIcon,
      accentColor: 'var(--class-confidential-text)'
    },
    {
      name: 'SCG',
      description: 'Manage classification guides',
      href: '/scg',
      icon: CogIcon,
      accentColor: 'var(--class-secret-text)'
    },
    {
      name: 'Testing',
      description: 'Validate classification accuracy',
      href: '/testing',
      icon: BeakerIcon,
      accentColor: 'var(--info)'
    }
  ];

  const getClassificationStats = () => {
    if (!documentsData?.documents) return {};
    const stats = {};
    documentsData.documents.forEach(doc => {
      const classification = doc.classification?.toUpperCase() || 'UNKNOWN';
      stats[classification] = (stats[classification] || 0) + 1;
    });
    return stats;
  };

  const classificationStats = getClassificationStats();
  const isHealthy = healthData?.status === 'healthy';

  // Animated metrics
  const totalQueries = useCountUp(queryAnalytics?.total_queries || 0, 800);
  const totalRules = useCountUp(scgData?.total_rules || 0, 1000);
  const avgExecRaw = typeof queryAnalytics?.avg_execution_time_ms === 'object'
    ? (queryAnalytics?.avg_execution_time_ms?.classification || 0)
    : (queryAnalytics?.avg_execution_time_ms || 0);
  const avgResponseTime = useCountUp(Math.round(avgExecRaw), 700);
  const totalCostRaw = costAnalytics?.total_estimated_cost || 0;
  const totalCostDisplay = typeof totalCostRaw === 'number' ? `$${totalCostRaw.toFixed(2)}` : '$0.00';

  return (
    <div className="space-y-6 pb-8">
      {/* Header Section */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">
              Operations Center
            </h1>
            <span className={`status-indicator ${isHealthy ? 'status-healthy' : 'status-error'} status-pulse`}>
              {isHealthy ? 'Operational' : 'Degraded'}
            </span>
          </div>
          <div className="hero-accent" />
          <p className="text-sm text-[var(--text-muted)] font-mono mt-3">
            Document Classification & Intelligence Processing System
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono text-[var(--text-muted)] uppercase tracking-widest">
            Last Sync
          </div>
          <div className="text-sm font-mono text-[var(--text-secondary)]">
            {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Metrics Row - Staggered entrance */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { value: totalQueries, label: 'Total Queries', color: 'var(--spotlight-gold)' },
          { value: totalRules, label: 'SCG Rules' },
          { value: `${avgResponseTime}ms`, label: 'Avg Response', color: 'var(--class-unclassified-text)' },
          { value: totalCostDisplay, label: 'LLM Cost', color: 'var(--class-secret-text)' },
        ].map((metric, index) => (
          <div
            key={metric.label}
            className="metric-card stagger-fade-in"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className="metric-value" style={metric.color ? { color: metric.color } : undefined}>
              {metric.value}
            </div>
            <div className="metric-label">{metric.label}</div>
          </div>
        ))}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions - Takes 2 columns */}
        <div className="lg:col-span-2 vault-card p-5">
          <div className="section-header">
            <h2>Quick Actions</h2>
            <span className="control-number">QA-001</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {quickActions.map((action) => (
              <Link
                key={action.name}
                to={action.href}
                className="action-tile group"
              >
                <div
                  className="action-tile-icon"
                  style={{ color: action.accentColor }}
                >
                  <action.icon className="w-5 h-5" />
                </div>
                <div className="action-tile-title group-hover:text-[var(--spotlight-gold)]">
                  {action.name}
                </div>
                <div className="action-tile-desc">
                  {action.description}
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* System Status */}
        <div className="vault-card p-5">
          <div className="section-header">
            <h2>System Status</h2>
            <span className="control-number">SYS-001</span>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-[var(--vault-bg)] rounded border border-[var(--vault-border)]">
              <div className="flex items-center gap-3">
                <ServerIcon className="w-4 h-4 text-[var(--text-muted)]" />
                <span className="text-sm text-[var(--text-secondary)]">API Server</span>
              </div>
              <span className={`text-xs font-mono uppercase ${isHealthy ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                {isHealthy ? 'Online' : 'Offline'}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-[var(--vault-bg)] rounded border border-[var(--vault-border)]">
              <div className="flex items-center gap-3">
                <CircleStackIcon className="w-4 h-4 text-[var(--text-muted)]" />
                <span className="text-sm text-[var(--text-secondary)]">Vector Store</span>
              </div>
              <span className="text-xs font-mono uppercase text-[var(--success)]">Online</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-[var(--vault-bg)] rounded border border-[var(--vault-border)]">
              <div className="flex items-center gap-3">
                <CpuChipIcon className="w-4 h-4 text-[var(--text-muted)]" />
                <span className="text-sm text-[var(--text-secondary)]">LLM Provider</span>
              </div>
              <span className="text-xs font-mono uppercase text-[var(--success)]">Groq</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-[var(--vault-bg)] rounded border border-[var(--vault-border)]">
              <div className="flex items-center gap-3">
                <ShieldCheckIcon className="w-4 h-4 text-[var(--text-muted)]" />
                <span className="text-sm text-[var(--text-secondary)]">SCG Index</span>
              </div>
              <span className="text-xs font-mono text-[var(--spotlight-gold)]">
                {scgData?.total_rules || 0} rules
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Classification Distribution */}
        <div className="vault-card p-5">
          <div className="section-header">
            <h2>Classification Distribution</h2>
            <span className="control-number">DIST-001</span>
          </div>
          {Object.keys(classificationStats).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(classificationStats).map(([classification, count]) => {
                const total = Object.values(classificationStats).reduce((a, b) => a + b, 0);
                const percentage = Math.round((count / total) * 100);
                return (
                  <div key={classification} className="vault-list-item">
                    <div className="flex items-center gap-3">
                      <ClassificationBadge classification={classification} />
                      <span className="text-sm font-mono text-[var(--text-muted)]">
                        {percentage}%
                      </span>
                    </div>
                    <span className="text-sm font-mono text-[var(--text-primary)]">
                      {count} docs
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-[var(--text-muted)]">
              <DocumentTextIcon className="w-8 h-8 mx-auto mb-3 opacity-50" />
              <p className="text-sm mb-4">No documents classified yet</p>
              <Link
                to="/classify"
                className="vault-btn vault-btn-primary text-xs"
              >
                Classify First Document
              </Link>
            </div>
          )}
        </div>

        {/* Recent Documents */}
        <div className="vault-card p-5">
          <div className="section-header">
            <div className="flex items-center justify-between w-full">
              <h2>Recent Activity</h2>
              <Link
                to="/classify"
                className="text-xs font-mono uppercase tracking-wider text-[var(--spotlight-gold)] hover:text-[var(--spotlight-amber)]"
              >
                View All →
              </Link>
            </div>
          </div>
          {documentsData?.documents?.length > 0 ? (
            <div className="space-y-2">
              {documentsData.documents
                .filter(doc => {
                  // Filter out empty placeholder entries (0 words and no title)
                  const wordCount = doc.metadata?.word_count || 0;
                  const hasTitle = doc.title && doc.title !== 'Untitled Document';
                  return wordCount > 0 || hasTitle;
                })
                .slice(0, 5)
                .map((doc, index) => (
                <div key={index} className="vault-list-item">
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-sm text-[var(--text-primary)] truncate">
                      {doc.title || 'Untitled Document'}
                    </p>
                    <p className="text-xs font-mono text-[var(--text-muted)]">
                      {doc.metadata?.word_count ? `${doc.metadata.word_count} words` :
                       doc.metadata?.timestamp ? new Date(doc.metadata.timestamp).toLocaleDateString() :
                       'Document'}
                    </p>
                  </div>
                  <ClassificationBadge
                    classification={doc.classification}
                    fullClassification={doc.full_classification}
                    caveats={doc.caveats}
                    releasability={doc.releasability}
                    confidence={doc.confidence}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[var(--text-muted)]">
              <ClockIcon className="w-8 h-8 mx-auto mb-3 opacity-50" />
              <p className="text-sm mb-4">No recent activity</p>
              <Link
                to="/scg"
                className="vault-btn vault-btn-secondary text-xs"
              >
                Upload SCG Guide
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Footer Info */}
      <div className="flex items-center justify-between pt-4 border-t border-[var(--vault-border)]">
        <div className="flex items-center gap-4">
          <span className="text-[10px] font-mono text-[var(--text-muted)] uppercase tracking-widest">
            Cognitive Fabric Platform v1.0
          </span>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-mono text-[var(--text-muted)]">
          <ArrowTrendingUpIcon className="w-3 h-3" />
          <span>System uptime: 99.9%</span>
        </div>
      </div>
    </div>
  );
}
